import { 
    Box, 
    Typography, 
    Button, 
    Card,
    CardContent,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tabs,
    Tab,
    Alert,
    Badge
} from "@mui/material";
import { useState } from "react";
import { useTheme } from "@mui/material";
import moment from "moment";
import GetAppIcon from '@mui/icons-material/GetApp';
import WarningIcon from '@mui/icons-material/Warning';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

import { useGetOvertimeExceptionReportQuery } from "../../state/api";

const OvertimeExceptions = () => {
    const theme = useTheme();
    
    const [selectedPeriod, setSelectedPeriod] = useState({
        startDate: moment().startOf('week').add(1, 'day').format('YYYY-MM-DD'), // Monday
        endDate: moment().endOf('week').add(1, 'day').format('YYYY-MM-DD') // Sunday
    });
    
    const [selectedDepartment, setSelectedDepartment] = useState('all');
    const [activeTab, setActiveTab] = useState(0);
    
    const { data: exceptionData, isLoading, isError, refetch } = useGetOvertimeExceptionReportQuery({
        ...selectedPeriod,
        department: selectedDepartment
    });

    const handlePeriodChange = (field, value) => {
        setSelectedPeriod(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleExportData = () => {
        if (!exceptionData?.exceptions) return;
        
        const allExceptions = [
            ...exceptionData.exceptions.overtime.map(ex => ({ ...ex, type: 'Overtime' })),
            ...exceptionData.exceptions.lateArrivals.map(ex => ({ ...ex, type: 'Late Arrival' })),
            ...exceptionData.exceptions.earlyDepartures.map(ex => ({ ...ex, type: 'Early Departure' })),
            ...exceptionData.exceptions.longBreaks.map(ex => ({ ...ex, type: 'Long Break' })),
            ...exceptionData.exceptions.noShows.map(ex => ({ ...ex, type: 'No Show' }))
        ];
        
        const headers = ['Type', 'Employee Name', 'Department', 'Date', 'Details', 'Severity'];
        const csvContent = [
            headers.join(','),
            ...allExceptions.map(ex => [
                ex.type,
                ex.employeeName,
                ex.department || 'N/A',
                moment(ex.date || ex.scheduledDate).format('YYYY-MM-DD'),
                getExceptionDetails(ex),
                ex.severity
            ].join(','))
        ].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `overtime-exceptions-${selectedPeriod.startDate}-to-${selectedPeriod.endDate}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const getExceptionDetails = (exception) => {
        if (exception.overtimeHours) return `${exception.overtimeHours}h overtime`;
        if (exception.minutesLate) return `${exception.minutesLate} minutes late`;
        if (exception.minutesEarly) return `${exception.minutesEarly} minutes early`;
        if (exception.breakDuration) return `${exception.breakDuration}h break`;
        return 'No show';
    };

    const getSeverityColor = (severity) => {
        switch (severity) {
            case 'high': return 'error';
            case 'medium': return 'warning';
            default: return 'info';
        }
    };

    const departments = [...new Set([
        ...exceptionData?.exceptions?.overtime?.map(ex => ex.department) || [],
        ...exceptionData?.exceptions?.lateArrivals?.map(ex => ex.department) || [],
        ...exceptionData?.exceptions?.earlyDepartures?.map(ex => ex.department) || [],
        ...exceptionData?.exceptions?.longBreaks?.map(ex => ex.department) || [],
        ...exceptionData?.exceptions?.noShows?.map(ex => ex.department) || []
    ])].filter(Boolean);

    if (isLoading) {
        return <Typography>Loading overtime & exception report...</Typography>;
    }

    if (isError) {
        return <Typography color="error">Error loading exception data.</Typography>;
    }

    return (
        <Box m="1.5rem 2.5rem">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb="2rem">
                <Typography variant="h4" color={theme.palette.secondary[100]}>
                    Overtime & Exception Tracking
                </Typography>
                <Box display="flex" gap="1rem" alignItems="center">
                    <TextField
                        label="Start Date"
                        type="date"
                        value={selectedPeriod.startDate}
                        onChange={(e) => handlePeriodChange('startDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                    />
                    <TextField
                        label="End Date"
                        type="date"
                        value={selectedPeriod.endDate}
                        onChange={(e) => handlePeriodChange('endDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                    />
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Department</InputLabel>
                        <Select
                            value={selectedDepartment}
                            onChange={(e) => setSelectedDepartment(e.target.value)}
                            label="Department"
                        >
                            <MenuItem value="all">All Departments</MenuItem>
                            {departments.map(dept => (
                                <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant="outlined"
                        onClick={() => refetch()}
                        sx={{ color: theme.palette.secondary[100], borderColor: theme.palette.secondary[100] }}
                    >
                        Refresh
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<GetAppIcon />}
                        onClick={handleExportData}
                        sx={{
                            backgroundColor: theme.palette.secondary.light,
                            color: theme.palette.background.alt,
                        }}
                    >
                        Export CSV
                    </Button>
                </Box>
            </Box>

            {/* Alert for high severity exceptions */}
            {exceptionData?.summary?.highSeverityCount > 0 && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="body1">
                        <strong>{exceptionData.summary.highSeverityCount}</strong> high-severity exceptions require immediate attention!
                    </Typography>
                </Alert>
            )}

            {/* Summary Cards */}
            <Grid container spacing={3} mb="2rem">
                <Grid item xs={12} sm={6} md={2}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap="1rem">
                                <WarningIcon color="error" />
                                <Box>
                                    <Typography variant="h6" color="error">
                                        {exceptionData?.summary?.totalExceptions || 0}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Total Exceptions
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap="1rem">
                                <TrendingUpIcon color="warning" />
                                <Box>
                                    <Typography variant="h6" color="warning.main">
                                        {exceptionData?.summary?.overtimeCount || 0}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Overtime
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap="1rem">
                                <AccessTimeIcon color="info" />
                                <Box>
                                    <Typography variant="h6" color="info.main">
                                        {exceptionData?.summary?.lateArrivalsCount || 0}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Late Arrivals
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap="1rem">
                                <AccessTimeIcon color="secondary" />
                                <Box>
                                    <Typography variant="h6" color="secondary">
                                        {exceptionData?.summary?.earlyDeparturesCount || 0}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Early Departures
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap="1rem">
                                <AccessTimeIcon color="primary" />
                                <Box>
                                    <Typography variant="h6" color="primary">
                                        {exceptionData?.summary?.longBreaksCount || 0}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Long Breaks
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" gap="1rem">
                                <PersonOffIcon color="error" />
                                <Box>
                                    <Typography variant="h6" color="error">
                                        {exceptionData?.summary?.noShowsCount || 0}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        No Shows
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Exception Details Tabs */}
            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                    <Tab label={
                        <Badge badgeContent={exceptionData?.summary?.overtimeCount || 0} color="warning">
                            Overtime
                        </Badge>
                    } />
                    <Tab label={
                        <Badge badgeContent={exceptionData?.summary?.lateArrivalsCount || 0} color="info">
                            Late Arrivals
                        </Badge>
                    } />
                    <Tab label={
                        <Badge badgeContent={exceptionData?.summary?.earlyDeparturesCount || 0} color="secondary">
                            Early Departures
                        </Badge>
                    } />
                    <Tab label={
                        <Badge badgeContent={exceptionData?.summary?.longBreaksCount || 0} color="primary">
                            Long Breaks
                        </Badge>
                    } />
                    <Tab label={
                        <Badge badgeContent={exceptionData?.summary?.noShowsCount || 0} color="error">
                            No Shows
                        </Badge>
                    } />
                </Tabs>

                {/* Overtime Tab */}
                {activeTab === 0 && (
                    <TableContainer sx={{ maxHeight: 600 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Employee</TableCell>
                                    <TableCell>Department</TableCell>
                                    <TableCell align="right">Total Hours</TableCell>
                                    <TableCell align="right">Overtime Hours</TableCell>
                                    <TableCell align="right">Threshold</TableCell>
                                    <TableCell>Week Period</TableCell>
                                    <TableCell>Severity</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {exceptionData?.exceptions?.overtime?.map((exception, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>
                                            <Typography variant="body1" fontWeight="bold">
                                                {exception.employeeName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={exception.department || 'N/A'} 
                                                size="small" 
                                                color="primary" 
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2">
                                                {exception.totalHours}h
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" color="warning.main" fontWeight="bold">
                                                {exception.overtimeHours}h
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" color="textSecondary">
                                                {exception.overtimeThreshold}h
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {moment(exception.weekStart).format('MMM DD')} - {moment(exception.weekEnd).format('MMM DD')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={exception.severity} 
                                                size="small" 
                                                color={getSeverityColor(exception.severity)}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* Late Arrivals Tab */}
                {activeTab === 1 && (
                    <TableContainer sx={{ maxHeight: 600 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Employee</TableCell>
                                    <TableCell>Department</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Scheduled Time</TableCell>
                                    <TableCell>Actual Time</TableCell>
                                    <TableCell align="right">Minutes Late</TableCell>
                                    <TableCell>Severity</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {exceptionData?.exceptions?.lateArrivals?.map((exception, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>
                                            <Typography variant="body1" fontWeight="bold">
                                                {exception.employeeName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={exception.department || 'N/A'} 
                                                size="small" 
                                                color="primary" 
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {moment(exception.date).format('MMM DD, YYYY')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {moment(exception.scheduledTime).format('HH:mm')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="warning.main">
                                                {moment(exception.actualTime).format('HH:mm')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" color="error" fontWeight="bold">
                                                {exception.minutesLate}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={exception.severity} 
                                                size="small" 
                                                color={getSeverityColor(exception.severity)}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* Early Departures Tab */}
                {activeTab === 2 && (
                    <TableContainer sx={{ maxHeight: 600 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Employee</TableCell>
                                    <TableCell>Department</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell>Scheduled End</TableCell>
                                    <TableCell>Actual End</TableCell>
                                    <TableCell align="right">Minutes Early</TableCell>
                                    <TableCell>Severity</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {exceptionData?.exceptions?.earlyDepartures?.map((exception, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>
                                            <Typography variant="body1" fontWeight="bold">
                                                {exception.employeeName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={exception.department || 'N/A'} 
                                                size="small" 
                                                color="primary" 
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {moment(exception.date).format('MMM DD, YYYY')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {moment(exception.scheduledEndTime).format('HH:mm')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="warning.main">
                                                {moment(exception.actualEndTime).format('HH:mm')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" color="error" fontWeight="bold">
                                                {exception.minutesEarly}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={exception.severity} 
                                                size="small" 
                                                color={getSeverityColor(exception.severity)}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* Long Breaks Tab */}
                {activeTab === 3 && (
                    <TableContainer sx={{ maxHeight: 600 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Employee</TableCell>
                                    <TableCell>Department</TableCell>
                                    <TableCell>Date</TableCell>
                                    <TableCell align="right">Break Duration</TableCell>
                                    <TableCell>Severity</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {exceptionData?.exceptions?.longBreaks?.map((exception, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>
                                            <Typography variant="body1" fontWeight="bold">
                                                {exception.employeeName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={exception.department || 'N/A'} 
                                                size="small" 
                                                color="primary" 
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {moment(exception.date).format('MMM DD, YYYY')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="body2" color="warning.main" fontWeight="bold">
                                                {exception.breakDuration}h
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={exception.severity} 
                                                size="small" 
                                                color={getSeverityColor(exception.severity)}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {/* No Shows Tab */}
                {activeTab === 4 && (
                    <TableContainer sx={{ maxHeight: 600 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Employee</TableCell>
                                    <TableCell>Department</TableCell>
                                    <TableCell>Scheduled Date</TableCell>
                                    <TableCell>Scheduled Time</TableCell>
                                    <TableCell>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {exceptionData?.exceptions?.noShows?.map((exception, index) => (
                                    <TableRow key={index} hover>
                                        <TableCell>
                                            <Typography variant="body1" fontWeight="bold">
                                                {exception.employeeName}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={exception.department || 'N/A'} 
                                                size="small" 
                                                color="primary" 
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {moment(exception.scheduledDate).format('MMM DD, YYYY')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {moment(exception.scheduledStart).format('HH:mm')} - {moment(exception.scheduledEnd).format('HH:mm')}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip 
                                                label="No Show" 
                                                size="small" 
                                                color="error"
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );
};

export default OvertimeExceptions; 